/**
 * Pure calculation logic for the appliance running-cost calculator
 * (content/pages/appliance-running-costs.md). Formula is deliberately the
 * simplest possible one, shown inline on the page rather than hidden:
 * watts / 1000 * hours = kWh, kWh * unit rate = cost.
 *
 * Preset wattages are typical UK figures, not name-plate maximums where the
 * two differ meaningfully (a washing machine's headline 2,000W+ rating is
 * its heating element at full draw for a few minutes, not the average pull
 * across a whole cycle), so a default run of the calculator lands close to
 * real-world cycle costs rather than overstating them.
 */

import { UNIT_RATE_PENCE } from "./energy-calculator.ts";

export interface AppliancePreset {
  id: string;
  label: string;
  watts: number;
  /** Typical hours per single use (a wash cycle, a shower, an evening's TV). For always-on appliances (fridge-freezer) this is 24. */
  defaultHoursPerUse: number;
  /** Typical times used per day, can be fractional (0.3 for "about twice a week"). */
  defaultUsesPerDay: number;
  /** Flagged as one of the appliances big enough to be worth checking against a solar/battery estimate. */
  highLoad?: boolean;
}

export const APPLIANCE_PRESETS: AppliancePreset[] = [
  { id: "kettle", label: "Kettle", watts: 3000, defaultHoursPerUse: 0.08, defaultUsesPerDay: 4 },
  { id: "washing-machine", label: "Washing machine (eco cycle)", watts: 450, defaultHoursPerUse: 1.5, defaultUsesPerDay: 0.5 },
  { id: "tumble-dryer", label: "Tumble dryer", watts: 2500, defaultHoursPerUse: 1, defaultUsesPerDay: 0.3 },
  { id: "dishwasher", label: "Dishwasher (eco cycle)", watts: 900, defaultHoursPerUse: 1.5, defaultUsesPerDay: 0.5 },
  { id: "oven", label: "Electric oven", watts: 2200, defaultHoursPerUse: 1, defaultUsesPerDay: 0.3 },
  { id: "ev-charger", label: "EV charger (home, 7kW)", watts: 7000, defaultHoursPerUse: 3, defaultUsesPerDay: 0.3, highLoad: true },
  { id: "immersion-heater", label: "Immersion heater", watts: 3000, defaultHoursPerUse: 1, defaultUsesPerDay: 0.14, highLoad: true },
  { id: "electric-shower", label: "Electric shower", watts: 8500, defaultHoursPerUse: 0.15, defaultUsesPerDay: 2, highLoad: true },
  // 30W is the average draw once the compressor's duty cycle is factored in, not its
  // nameplate/running wattage (nearer 100-150W): at 24h/day that lands on the ~200-350
  // kWh/year a modern UK fridge-freezer typically uses, rather than several times that.
  { id: "fridge-freezer", label: "Fridge-freezer (always on)", watts: 30, defaultHoursPerUse: 24, defaultUsesPerDay: 1 },
  { id: "tv", label: "TV", watts: 100, defaultHoursPerUse: 4, defaultUsesPerDay: 1 },
  { id: "games-console", label: "Games console", watts: 150, defaultHoursPerUse: 2, defaultUsesPerDay: 1 },
  { id: "custom", label: "Other (enter your own wattage)", watts: 1000, defaultHoursPerUse: 1, defaultUsesPerDay: 1 },
];

export function findAppliancePreset(id: string): AppliancePreset | undefined {
  return APPLIANCE_PRESETS.find((preset) => preset.id === id);
}

export interface ApplianceCostInput {
  watts: number;
  hoursPerUse: number;
  usesPerDay: number;
}

export interface ApplianceCostEstimate {
  kwhPerUse: number;
  costPerUsePence: number;
  costPerDayPence: number;
  costPerYearPence: number;
}

/** A negative or non-finite input (blank field, stray minus sign) is treated as 0 rather than propagating NaN/negative costs through the page. */
function clampNonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function computeApplianceCost(input: ApplianceCostInput): ApplianceCostEstimate {
  const watts = clampNonNegative(input.watts);
  const hoursPerUse = clampNonNegative(input.hoursPerUse);
  const usesPerDay = clampNonNegative(input.usesPerDay);

  const kwhPerUse = (watts / 1000) * hoursPerUse;
  const costPerUsePence = kwhPerUse * UNIT_RATE_PENCE;
  const costPerDayPence = costPerUsePence * usesPerDay;
  const costPerYearPence = costPerDayPence * 365;

  return { kwhPerUse, costPerUsePence, costPerDayPence, costPerYearPence };
}
